import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { FileText, Search, RefreshCw } from 'lucide-react';
import { Input } from '../components/ui/input';
import { formatDate } from '../utils/dateUtils';
import DevisCreateModal from './DevisCreateModal';
import { STATUTS } from './devis/statuts';
import { LifecycleBarD } from './devis/LifecycleBarD';
import DevisEditor from './devis/DevisEditor';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── CHIPS DE FILTRE (ANO-027 : alignés sur l'enum backend QuoteStatus + statuts posés en code) ───
const FILTER_CHIPS = [
  { key: '',             label: 'Toutes' },
  { key: 'brouillon',    label: 'Brouillons' },
  { key: 'envoye',       label: 'Envoyés' },
  { key: 'signe',        label: 'Signés' },
  { key: 'valide_admin', label: 'Validés' },
  { key: 'acompte_paye', label: 'Acompte payé' },
  { key: 'solde_paye',   label: 'Soldés' },
  { key: 'annule',       label: 'Annulés' },
];

// ─── Page principale AdminDevis ───────────────────────────────────────────────
export default function AdminDevis() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/devis?limit=100`;
      if (statusFilter) url += `&statut=${statusFilter}`;
      const [qRes, sRes] = await Promise.all([
        fetch(url, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/devis/stats/admin`, { headers: H }).then(r => r.json()),
      ]);
      let qs = qRes.quotes || [];
      if (search) qs = qs.filter(q =>
        [q.client_nom, q.client_email, q.numero].some(v => v?.toLowerCase().includes(search.toLowerCase()))
      );
      setQuotes(qs);
      setStats(sRes);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  // ANO-025 : ouverture directe de la FICHE devis via ?open={id} (CTA des e-mails admin).
  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get('open');
    if (openId) openFull({ id: openId });
  }, []);

  async function openFull(q) {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${q.id}/full`, { headers: H });
      const data = await res.json();
      setSelectedQuote(data);
    } catch { setSelectedQuote(q); }
  }

  return (
    <div data-testid="admin-devis">
      {showCreate && (
        <DevisCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Commerce</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Commandes</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              Devis et réservations — filtrez par étape du cycle
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowCreate(true)} data-testid="nouveau-devis-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                border: 'none', background: 'var(--admin-gold)', color: '#2a1d05',
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              <FileText size={13}/> Nouveau devis
            </button>
            <button onClick={load}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                border: '1px solid var(--admin-border)', background: 'var(--admin-surface)',
                color: 'var(--admin-muted)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              <RefreshCw size={13}/> Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Stats KPI */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Total devis',    value: stats.total_devis,                          color: 'var(--admin-blue)' },
            { label: 'CA encaissé',    value: `${stats.ca_total || 0}€`,                  color: 'var(--admin-gold)' },
            { label: 'Signés/Payés',   value: (stats.devis_signe||0)+(stats.devis_solde||0), color: 'var(--admin-green)' },
            { label: 'Panier moyen',   value: `${stats.panier_moyen || 0}€`,              color: 'var(--admin-amber)' },
          ].map((s, i) => (
            <div key={i} className="adm-card" style={{ padding: '12px 14px' }}>
              <p style={{ fontSize: 11.5, color: 'var(--admin-faint)', marginBottom: 4 }}>{s.label}</p>
              <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 20, fontWeight: 700, color: s.color }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Chips filtre + recherche */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12,
        scrollbarWidth: 'none' }}>
        {FILTER_CHIPS.map(chip => (
          <button key={chip.key} onClick={() => setStatusFilter(chip.key)}
            style={{
              flexShrink: 0, fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 9,
              border: `1px solid ${statusFilter === chip.key ? 'var(--admin-teal-line)' : 'var(--admin-border)'}`,
              background: statusFilter === chip.key ? 'var(--admin-teal-dim)' : 'var(--admin-surface)',
              color: statusFilter === chip.key ? 'var(--admin-teal)' : 'var(--admin-muted)',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
            {chip.label}
          </button>
        ))}
      </div>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--admin-faint)' }} />
        <Input placeholder="Rechercher client, email, n°..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 34, background: 'var(--admin-surface)',
            borderColor: 'var(--admin-border-soft)', color: 'var(--admin-text)', height: 38, fontSize: 13.5 }} />
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1,2,3,4].map(i => (
            <div key={i} className="adm-card" style={{ height: 80, animation: 'pulse 1.5s infinite',
              background: 'var(--admin-surface2)' }} />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--admin-faint)' }}>
          <FileText size={36} style={{ margin: '0 auto 12px', opacity: .25 }} />
          <p style={{ fontSize: 14 }}>Aucun devis trouvé</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {quotes.map(q => {
            const s = STATUTS[q.statut] || STATUTS.brouillon;
            const sess = q.session || {};
            const dateEv = q.date_livraison || sess.date_livraison || sess.date_evenement || '';
            const adr = (q.adresse_livraison || sess.adresse || '').split(',')[0];
            return (
              <div key={q.id}
                className="adm-card"
                style={{ padding: '14px 16px', cursor: 'pointer',
                  transition: 'border-color .15s, transform .12s' }}
                onClick={() => openFull(q)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--admin-teal-line)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--admin-border-soft)'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
                      <span style={{ fontFamily: 'var(--admin-font-display)', fontSize: 11, fontWeight: 700,
                        color: 'var(--admin-gold)', letterSpacing: '.04em' }}>{q.numero}</span>
                    </div>
                    <p style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 }}>
                      {q.client_nom || q.client_email}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
                      {adr ? `${adr} · ` : ''}{formatDate(q.created_at)}
                      {dateEv ? ` · Év. ${formatDate(dateEv)}` : ''}
                      {` · ${(q.items||[]).length} article${(q.items||[]).length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 15, fontWeight: 700,
                      color: 'var(--admin-text)' }}>
                      {q.total ? `${parseFloat(q.total).toFixed(0)}€` : '—'}
                    </p>
                  </div>
                </div>
                <LifecycleBarD statut={q.statut} />
              </div>
            );
          })}
        </div>
      )}

      {/* Modal éditeur (logique conservée intacte) */}
      {selectedQuote && (
        <DevisEditor
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onSaved={() => { load(); setSelectedQuote(null); }}
          onReload={() => { openFull(selectedQuote); load(); }}
        />
      )}
    </div>
  );
}

